import { localizarPontoNoMundo, pontosDeAncoragem, pontosNoMundo } from './ancoragem'
import { adicionarCorda } from './cordas'
import { fitaAbraca, MEIO_MOSQUETAO_M } from './fitaDeAncoragem'
import { criarAncoragemAutomatica } from './geracaoAncoragens'
import { adicionarObjeto } from './operacoes'
import type { CenaTabletop, GeradorIdTabletop, ReferenciaPonto, VetorTabletop } from './tipos'

/**
 * Prender uma peça (ou amarrar um nó fiel) num ponto de ancoragem, a partir do ponto: o instrutor toca
 * no degrau, no toco ou no olhal e escolhe "clipar mosquetão" ou "pendurar
 * polia". A montagem segue a prática:
 *
 *  - numa ESTRUTURA (escada, toco, viatura, árvore…), a peça não se prende
 *    direto: uma fita abraça o ponto e a peça pende dela, 35 cm abaixo
 *    (a polia clipa na fita pelo mosquetão que a fita já desenha no olhal);
 *  - numa peça com OLHAL ou num MOSQUETÃO, a peça pende direto dele, sem fita.
 *
 * Tudo numa alteração só da cena (um passo de desfazer). TypeScript puro.
 */

import { juntarFitasEmPonto, podeJuntarFitas } from './ancoragensDeFitas'

/**
 * `noFiel` amarra a ponta da corda direto no ponto (sem fita): a volta do fiel numa barra, como o degrau da escada.
 * `noOito` amarra a alça do nó no ponto (com mosquetão ou direto).
 * `poliaDupla` pendura a polia dupla pelo olhal.
 * `sistemaVM` pendura o sistema de vantagem pelo olhal do bloco fixo (numa estrutura, por uma fita).
 */
export type PecaParaPrender = 'mosquetao' | 'polia' | 'poliaDupla' | 'noFiel' | 'noOito' | 'sistemaVM'

/** Quanto a peça pende abaixo do ponto, na ponta da fita. */
export const QUEDA_NA_FITA_M = 0.35
/** Do centro da polia ao olhal dela (ver `ancoragem.ts`). */
const OLHAL_DA_POLIA_M = 0.075
/** A peça não desce abaixo disto: no pé de um toco, ela vai para o lado. */
const ALTURA_MINIMA_M = 0.1

export interface ResultadoPrender {
  readonly cena: CenaTabletop
  /** A peça nova, para ficar selecionada. */
  readonly idPeca: string
  /** A fita criada entre o ponto e a peça, quando o ponto é de uma estrutura. */
  readonly idFita: string | null
}

const somar = (a: VetorTabletop, b: VetorTabletop): VetorTabletop => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]

/** Onde fica o ponto da peça por onde ela se prende (o centro do mosquetão, o olhal da polia). */
function pontoDePrender(cena: CenaTabletop, referencia: ReferenciaPonto, comFita: boolean): VetorTabletop | null {
  const ponto = localizarPontoNoMundo(cena, referencia)
  if (!ponto) return null
  const objeto = cena.objetos.find((o) => o.id === referencia.idObjeto)!
  if (!comFita) {
    // pendurada direto: num mosquetão, abaixo da barra de baixo dele; num olhal, logo abaixo
    const queda = objeto.tipo === 'mosquetao' ? 2 * MEIO_MOSQUETAO_M : MEIO_MOSQUETAO_M
    return somar(ponto, [0, -queda, 0])
  }
  // na ponta da fita, abaixo do ponto; perto do chão, para o lado
  if (ponto[1] - QUEDA_NA_FITA_M >= ALTURA_MINIMA_M) return somar(ponto, [0, -QUEDA_NA_FITA_M, 0])
  return somar(ponto, [QUEDA_NA_FITA_M, 0, 0])
}

/** A alça de fita: o laço da própria fita, que não tem peça por trás (ver `criarAncoragemAutomatica`). */
const ehAlcaDeFita = (objeto: { tipo: string; parametros?: Readonly<Record<string, number>> }) =>
  objeto.tipo === 'pontoAncoragem' && objeto.parametros?.alcaFita === 1

const mesmaRef = (a: ReferenciaPonto, b: ReferenciaPonto) => a.idObjeto === b.idObjeto && a.idPonto === b.idPonto

/**
 * Um mosquetão clipado na alça de fita TOMA o lugar dela: a alça some, e as
 * fitas, cordas e ancoragens que terminavam nela passam a terminar no
 * mosquetão. Sobra só o mosquetão, onde mais fitas podem chegar.
 */
function trocarAlcaPorMosquetao(cena: CenaTabletop, referencia: ReferenciaPonto, gerarId: GeradorIdTabletop): ResultadoPrender {
  const posicao = localizarPontoNoMundo(cena, referencia)!
  const idPeca = gerarId()
  const novoPonto: ReferenciaPonto = { idObjeto: idPeca, idPonto: 'passagem' }
  const troca = (r: ReferenciaPonto) => (mesmaRef(r, referencia) ? novoPonto : r)
  const comMosquetao = adicionarObjeto(cena, { tipo: 'mosquetao', transformacao: { posicao, rotacao: [0, 0, 0], escala: [1, 1, 1] } }, () => idPeca)
  const nova: CenaTabletop = {
    ...comMosquetao,
    objetos: comMosquetao.objetos.filter((o) => o.id !== referencia.idObjeto),
    cordas: (comMosquetao.cordas ?? []).map((c) => ({ ...c, percurso: c.percurso.map(troca) })),
    ancoragens: (comMosquetao.ancoragens ?? []).map((a) => ({ ...a, centro: troca(a.centro), fixacoes: a.fixacoes.map(troca) })),
  }
  return { cena: nova, idPeca, idFita: null }
}

export function prenderPecaNoPonto(
  cena: CenaTabletop,
  referencia: ReferenciaPonto,
  peca: PecaParaPrender,
  gerarId: GeradorIdTabletop,
  parametros?: Record<string, number>,
): ResultadoPrender {
  const objeto = cena.objetos.find((o) => o.id === referencia.idObjeto)
  if (!objeto || !pontosDeAncoragem(objeto).some((p) => p.id === referencia.idPonto)) {
    throw new Error('O ponto escolhido não existe mais na cena.')
  }
  if (peca === 'mosquetao' && ehAlcaDeFita(objeto)) return trocarAlcaPorMosquetao(cena, referencia, gerarId)
  // os nós são a própria amarração: ficam sobre o ponto, sem fita
  const comFita = peca !== 'noFiel' && peca !== 'noOito' && fitaAbraca(objeto)
  const alvo = peca === 'noFiel' || peca === 'noOito' ? localizarPontoNoMundo(cena, referencia)! : pontoDePrender(cena, referencia, comFita)!
  const idPeca = gerarId()
  // o mosquetão se prende pelo centro (a passagem); a polia, pelo olhal, 7,5 cm acima do centro;
  // o sistema de vantagem, pelo olhal do bloco fixo, que é a origem dele
  const posicao = peca === 'polia' || peca === 'poliaDupla' ? somar(alvo, [0, -OLHAL_DA_POLIA_M, 0]) : alvo
  // o fiel acompanha o giro da estrutura (a barra do degrau fica ao longo de X)
  const rotacao: VetorTabletop = peca === 'noFiel' || peca === 'noOito' ? [0, objeto.transformacao.rotacao[1], 0] : [0, 0, 0]
  let nova = adicionarObjeto(cena, { tipo: peca, transformacao: { posicao, rotacao, escala: [1, 1, 1] }, parametros }, () => idPeca)
  let idFita: string | null = null
  if (comFita) {
    idFita = gerarId()
    nova = adicionarCorda(
      nova,
      { funcao: 'fita', percurso: [referencia, { idObjeto: idPeca, idPonto: pontoDaPeca(peca) }] },
      () => idFita!,
    )
  }
  return { cena: nova, idPeca, idFita }
}

/** Até esta distância do ponto de ancoragem, soltar um mosquetão, polia ou alça de fita encaixa a peça nele. */
export const RAIO_ENCAIXE_M = 0.45

export const ehPecaDeEncaixe = (tipo: string, parametros?: Readonly<Record<string, number>>): boolean =>
  tipo === 'mosquetao' || tipo === 'polia' || tipo === 'poliaDupla' || (tipo === 'pontoAncoragem' && parametros?.alcaFita === 1)

/** O ponto da peça por onde ela se prende: o centro do mosquetão, o olhal da polia. */
const pontoDaPeca = (peca: PecaParaPrender) =>
  peca === 'sistemaVM' ? 'ancoragem' : peca === 'polia' || peca === 'poliaDupla' || peca === 'noFiel' ? 'olhal' : 'passagem'

/**
 * Encontra o ponto de ancoragem mais perto da peça (dentro do raio de encaixe),
 * usado tanto para a prévia magnética durante o arraste quanto para o encaixe final.
 */
export function pontoDeEncaixeMaisPerto(
  cena: CenaTabletop,
  idPeca: string,
): { referencia: ReferenciaPonto; pontoMundo: VetorTabletop; distancia: number; proprioMundo: VetorTabletop; ehAlcaDeFita: boolean } | null {
  const objeto = cena.objetos.find((o) => o.id === idPeca)
  if (!objeto || !ehPecaDeEncaixe(objeto.tipo, objeto.parametros)) return null
  const peca = objeto.tipo
  const proprio = { idObjeto: idPeca, idPonto: ehAlcaDeFita(objeto) ? 'olhal' : pontoDaPeca(peca as PecaParaPrender) }
  const aqui = localizarPontoNoMundo(cena, proprio)
  if (!aqui) return null
  let melhor: { referencia: ReferenciaPonto; pontoMundo: VetorTabletop; distancia: number; proprioMundo: VetorTabletop; ehAlcaDeFita: boolean } | null = null
  for (const outro of cena.objetos) {
    if (outro.id === idPeca || outro.oculto) continue
    for (const p of pontosNoMundo(outro)) {
      if (ehAlcaDeFita(objeto)) {
        // Alça de fita só encaixa em outra ponta de fita com a qual pode juntar
        if (!podeJuntarFitas(cena, proprio, { idObjeto: outro.id, idPonto: p.id })) continue
      } else {
        if (p.uso === 'passagem' && outro.tipo !== 'mosquetao') continue
      }
      const d = Math.hypot(p.mundo[0] - aqui[0], p.mundo[1] - aqui[1], p.mundo[2] - aqui[2])
      if (d <= RAIO_ENCAIXE_M && (!melhor || d < melhor.distancia)) {
        melhor = { referencia: { idObjeto: outro.id, idPonto: p.id }, pontoMundo: p.mundo, distancia: d, proprioMundo: aqui, ehAlcaDeFita: ehAlcaDeFita(objeto) }
      }
    }
  }
  return melhor
}

/**
 * Encaixe ao soltar: o mosquetão ou a polia arrastado para perto (até
 * `RAIO_ENCAIXE_M`) de um ponto de ancoragem de outra peça se prende nele, do
 * mesmo jeito que "clipar mosquetão" e "pendurar polia" — numa estrutura, por
 * uma fita; num olhal ou num mosquetão, direto.
 * Duas alças de fita soltas uma perto da outra se fundem formando o ponto diretor em V.
 */
export function encaixarPecaNoPontoMaisPerto(cena: CenaTabletop, idPeca: string, gerarId: GeradorIdTabletop): CenaTabletop {
  const objeto = cena.objetos.find((o) => o.id === idPeca)
  if (!objeto || !ehPecaDeEncaixe(objeto.tipo, objeto.parametros)) return cena
  const ehAlca = ehAlcaDeFita(objeto)
  const peca = objeto.tipo
  const proprio = { idObjeto: idPeca, idPonto: ehAlca ? 'olhal' : pontoDaPeca(peca as PecaParaPrender) }
  const presa = !ehAlca && (
    (cena.cordas ?? []).some((c) => c.percurso.some((p) => p.idObjeto === idPeca)) ||
    (cena.ancoragens ?? []).some((a) => a.centro.idObjeto === idPeca || a.fixacoes.some((f) => f.idObjeto === idPeca))
  )
  if (presa) return cena
  const aqui = localizarPontoNoMundo(cena, proprio)
  if (!aqui) return cena
  let melhor: { referencia: ReferenciaPonto; distancia: number } | null = null
  for (const outro of cena.objetos) {
    if (outro.id === idPeca || outro.oculto) continue
    for (const p of pontosNoMundo(outro)) {
      if (ehAlca) {
        if (!podeJuntarFitas(cena, proprio, { idObjeto: outro.id, idPonto: p.id })) continue
      } else {
        if (p.uso === 'passagem' && outro.tipo !== 'mosquetao') continue
      }
      const d = Math.hypot(p.mundo[0] - aqui[0], p.mundo[1] - aqui[1], p.mundo[2] - aqui[2])
      if (d <= RAIO_ENCAIXE_M && (!melhor || d < melhor.distancia)) melhor = { referencia: { idObjeto: outro.id, idPonto: p.id }, distancia: d }
    }
  }
  if (!melhor) return cena

  if (ehAlca) {
    return juntarFitasEmPonto(cena, proprio, melhor.referencia, gerarId).cena
  }

  // a mesma montagem de "prender no ponto", com a peça que já existe: tira a peça, prende de novo e devolve o id
  const semAPeca = { ...cena, objetos: cena.objetos.filter((o) => o.id !== idPeca) }
  const ids = [idPeca, gerarId()]
  let k = 0
  const { cena: presaNoPonto } = prenderPecaNoPonto(semAPeca, melhor.referencia, peca as PecaParaPrender, () => ids[k++]!)
  // a peça volta com tudo o que tinha (nome, cor, parâmetros), só na posição nova
  const nova = presaNoPonto.objetos.find((o) => o.id === idPeca)!
  const restaurada = { ...presaNoPonto, objetos: presaNoPonto.objetos.map((o) => (o.id === idPeca ? { ...objeto, transformacao: { ...objeto.transformacao, posicao: nova.transformacao.posicao } } : o)) }
  // a ordem dos objetos não muda
  const ordenados = cena.objetos.flatMap((o) => restaurada.objetos.find((x) => x.id === o.id) ?? [])
  const novos = restaurada.objetos.filter((o) => !cena.objetos.some((x) => x.id === o.id))
  return { ...restaurada, objetos: [...ordenados, ...novos] }
}


/** Peças que têm olhal ou passagem: uma fita que termina nelas é um anel, não um V entre duas estruturas. */
const TIPOS_DE_PECA_COM_OLHAL: ReadonlySet<string> = new Set(['mosquetao', 'polia', 'poliaDupla', 'bloqueador', 'descensor', 'pontoAncoragem', 'placaAncoragem', 'noOito', 'noFiel', 'sistemaVM', 'maca'])

/**
 * Uma fita de dois pontos que liga duas ESTRUTURAS (os dois banzos da escada,
 * dois tocos) e ainda não tem onde clipar nada: ela vira o V de verdade (as
 * duas pernas até um vértice com argola) e o mosquetão entra no vértice.
 * Falha se a corda não é uma fita de dois pontos entre estruturas.
 */
export function fitaEmVComMosquetao(
  cena: CenaTabletop,
  idFita: string,
  gerarId: GeradorIdTabletop,
): { readonly cena: CenaTabletop; readonly idMosquetao: string; readonly idVertice: string } {
  const fita = cena.cordas?.find((c) => c.id === idFita)
  if (!fita || fita.funcao !== 'fita' || fita.percurso.length !== 2) throw new Error('Só uma fita de dois pontos vira um V.')
  const estruturas = fita.percurso.every((p) => {
    const o = cena.objetos.find((x) => x.id === p.idObjeto)
    return Boolean(o) && !TIPOS_DE_PECA_COM_OLHAL.has(o!.tipo)
  })
  if (!estruturas) throw new Error('A fita já termina numa peça: clipe o mosquetão nela.')
  const semFita = { ...cena, cordas: (cena.cordas ?? []).filter((c) => c.id !== idFita) }
  const v = criarAncoragemAutomatica(semFita, fita.percurso, 'V', gerarId)
  const r = prenderPecaNoPonto(v.cena, { idObjeto: v.idObjetoVertice, idPonto: 'olhal' }, 'mosquetao', gerarId)
  return { cena: r.cena, idMosquetao: r.idPeca, idVertice: v.idObjetoVertice }
}

/** A fita pode virar um V com mosquetão (dois pontos, os dois em estruturas)? */
export function fitaPodeVirarV(cena: CenaTabletop, idFita: string): boolean {
  try {
    fitaEmVComMosquetao(cena, idFita, (() => { let k = 0; return () => `teste-${k++}` })())
    return true
  } catch {
    return false
  }
}
